import { useRef, useState } from "react";
import { api, API, getUser, setUser } from "../lib/api";
import { openExternal } from "../lib/native";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { IdCard, FileCheck2, Landmark, Upload, Loader2, FileText, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// Task 8 — Carrier verification + bank details card. Sits alongside the
// existing licence + compliance cards on the Dashboard.
//
// Status model:
//   • not_uploaded  → red pill (also blocks bidding for Photo ID + bank)
//   • uploaded      → amber pill (default once submitted)
//   • verified      → green pill (future admin action; server sets the
//                     `*_verified` boolean when a reviewer approves it)
function statusOf(field, isUploaded, isVerified) {
  if (isVerified) return { key: "verified", label: "Verified", tone: "bg-[#4A6B56] text-white" };
  if (isUploaded) return { key: "uploaded", label: "Uploaded", tone: "bg-[#D67B54] text-white" };
  return { key: "not_uploaded", label: "Not uploaded", tone: "bg-[#EBE8E0] text-[#6B6965]" };
}

function DocRow({ testId, icon: Icon, title, hint, status, fileUrl, onFile, uploading, actionLabel }) {
  const inputRef = useRef();
  return (
    <div data-testid={testId} className="rounded-2xl border border-[#E2DFD8] bg-white p-4 space-y-2">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#1B3624] text-white flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>{title}</div>
            <Badge data-testid={`${testId}-status`} className={`uppercase text-[10px] tracking-wider ${status.tone}`}>{status.label}</Badge>
          </div>
          <p className="text-xs text-[#6B6965] mt-0.5">{hint}</p>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={(e) => onFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          data-testid={`${testId}-btn`}
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="rounded-full border-[#1B3624] text-[#1B3624] min-h-[40px]"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
          {actionLabel}
        </Button>
        {fileUrl && (
          <a
            href={`${API.replace(/\/api$/, "")}${fileUrl}`}
            onClick={(e) => {
              e.preventDefault();
              openExternal(`${API.replace(/\/api$/, "")}${fileUrl}`);
            }}
            data-testid={`${testId}-view`}
            className="inline-flex items-center gap-1 text-xs text-[#4A6B56] hover:underline"
          >
            <FileText className="w-3 h-3" /> View current
          </a>
        )}
      </div>
    </div>
  );
}

export default function CarrierKycCard({ onChanged }) {
  const user = getUser();
  const [uploading, setUploading] = useState({ photo_id: false, poa: false });
  const [saving, setSaving] = useState(false);
  const [bankForm, setBankForm] = useState({
    account_holder: user?.bank_account_holder || "",
    sort_code: user?.bank_sort_code || "",
    account_number: "", // never rehydrated — user re-enters if changing
  });

  if (!user || user.role !== "carrier") return null;

  const refresh = async () => {
    const { data: fresh } = await api.get(`/auth/me/${user.id}`);
    setUser(fresh);
    onChanged?.();
  };

  const uploadDoc = async (file, endpoint, key) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [key]: true }));
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("user_id", user.id);
      await api.post(endpoint, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [key]: false }));
    }
  };

  const saveBank = async () => {
    // Client-side quick sanity so the button feels responsive before the
    // server round-trip; the server re-validates.
    const digitsSort = (bankForm.sort_code || "").replace(/\D/g, "");
    const digitsAcc = (bankForm.account_number || "").replace(/\D/g, "");
    if (!bankForm.account_holder.trim()) return toast.error("Enter the account holder name");
    if (digitsSort.length !== 6) return toast.error("Sort code must be 6 digits");
    if (digitsAcc.length !== 8) return toast.error("Account number must be 8 digits");
    setSaving(true);
    try {
      await api.patch(
        `/carriers/${user.id}/bank-details`,
        {
          account_holder: bankForm.account_holder.trim(),
          sort_code: bankForm.sort_code,
          account_number: bankForm.account_number,
        },
        { params: { user_id: user.id } }
      );
      toast.success("Bank details saved");
      setBankForm((b) => ({ ...b, account_number: "" }));
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const photoStatus = statusOf("photo_id", !!user.photo_id_document_url, !!user.photo_id_verified);
  const poaStatus = statusOf("poa", !!user.proof_of_address_url, !!user.proof_of_address_verified);
  const bankStatus = statusOf(
    "bank",
    !!(user.bank_account_holder && user.bank_sort_code && user.bank_account_last4),
    !!user.bank_verified
  );

  const gatingMissing = [];
  if (!user.photo_id_document_url) gatingMissing.push("Photo ID");
  if (!(user.bank_account_holder && user.bank_sort_code && user.bank_account_last4)) gatingMissing.push("Bank details");

  return (
    <Card
      data-testid="carrier-kyc-card"
      className={`md:col-span-2 rounded-2xl border p-5 ${
        gatingMissing.length > 0 ? "border-[#D67B54] bg-[#FDF2EB]" : "border-[#4A6B56]/30 bg-[#4A6B56]/5"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
            gatingMissing.length > 0 ? "bg-[#D67B54] text-white" : "bg-[#4A6B56] text-white"
          }`}
        >
          {gatingMissing.length > 0 ? <AlertTriangle className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
        </div>
        <div className="flex-1">
          <div className="font-medium" style={{ fontFamily: "Outfit" }}>Verification &amp; bank details</div>
          {gatingMissing.length > 0 ? (
            <p data-testid="kyc-missing" className="text-sm text-[#1B3624] mt-1">
              Bidding needs at least{" "}
              <span className="font-medium">{gatingMissing.join(" and ")}</span>
              . Proof of address is optional but recommended.
            </p>
          ) : (
            <p className="text-sm text-[#6B6965] mt-1">All required KYC and payout details are on file — you're clear to bid.</p>
          )}
        </div>
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <DocRow
          testId="kyc-photo-id"
          icon={IdCard}
          title="Photo ID"
          hint="Driving licence or passport. PDF or image, max 8MB."
          status={photoStatus}
          fileUrl={user.photo_id_document_url}
          uploading={uploading.photo_id}
          actionLabel={user.photo_id_document_url ? "Replace" : "Upload Photo ID"}
          onFile={(f) => uploadDoc(f, "/carriers/photo-id", "photo_id")}
        />
        <DocRow
          testId="kyc-poa"
          icon={FileCheck2}
          title="Proof of address"
          hint="Utility bill or bank statement, dated within 3 months."
          status={poaStatus}
          fileUrl={user.proof_of_address_url}
          uploading={uploading.poa}
          actionLabel={user.proof_of_address_url ? "Replace" : "Upload proof of address"}
          onFile={(f) => uploadDoc(f, "/carriers/proof-of-address", "poa")}
        />
      </div>

      <div className="mt-4 rounded-2xl border border-[#E2DFD8] bg-white p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#1B3624] text-white flex items-center justify-center shrink-0">
            <Landmark className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-sm font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>Bank details for payouts</div>
              <Badge data-testid="kyc-bank-status" className={`uppercase text-[10px] tracking-wider ${bankStatus.tone}`}>{bankStatus.label}</Badge>
            </div>
            <p className="text-xs text-[#6B6965] mt-0.5">
              UK sort code + 8-digit account number. We store only the last four digits — the full number is never persisted.
            </p>
            {user.bank_account_last4 && (
              <p data-testid="kyc-bank-current" className="text-[11px] text-[#4A6B56] mt-1">
                On file: {user.bank_account_holder} · {user.bank_sort_code} · ••••{user.bank_account_last4}
              </p>
            )}
          </div>
        </div>
        <div className="mt-3 grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Account holder name</Label>
            <Input
              data-testid="kyc-bank-holder"
              value={bankForm.account_holder}
              onChange={(e) => setBankForm((b) => ({ ...b, account_holder: e.target.value }))}
              placeholder="As on the account"
              className="rounded-xl mt-1"
              maxLength={60}
            />
          </div>
          <div>
            <Label className="text-xs">Sort code</Label>
            <Input
              data-testid="kyc-bank-sort"
              value={bankForm.sort_code}
              onChange={(e) => setBankForm((b) => ({ ...b, sort_code: e.target.value }))}
              placeholder="12-34-56"
              className="rounded-xl mt-1"
              maxLength={8}
              inputMode="numeric"
            />
          </div>
          <div>
            <Label className="text-xs">Account number</Label>
            <Input
              data-testid="kyc-bank-account"
              value={bankForm.account_number}
              onChange={(e) => setBankForm((b) => ({ ...b, account_number: e.target.value }))}
              placeholder="8 digits"
              className="rounded-xl mt-1"
              maxLength={8}
              inputMode="numeric"
              type="password"
              autoComplete="off"
            />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button
            data-testid="kyc-bank-save"
            onClick={saveBank}
            disabled={saving}
            className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white px-6 min-h-[40px]"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save bank details"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
